import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/form/button';
import Checkbox from '../../../components/form/checkbox';
import Input from '../../../components/form/input';
import Modal from '../../../components/modal/default';
import { isValidPassword } from '../../../components/password-hints';
import useAuth from '../../../hooks/auth';
import useRouter from '../../../hooks/router';
import { logger } from '../../../utils/logger';
import styles from './styles.module.css';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const UsersActivateView: FC = () => {
  const [tried, setTried] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [error, setError] = useState<string>();
  const [pass, setPass] = useState('');
  const [nameFirst, setNameFirst] = useState('');
  const [nameLast, setNameLast] = useState('');
  const { logout, activate, isLoading, decode } = useAuth();
  const { query } = useRouter();
  const token = query.has('token') ? query.get('token') : undefined;
  const decoded: { email?: string } = token ? decode(token) : {};

  // clear out any current auth state, just in case
  useEffect(() => logout(), []);

  const handleSetPass = (e: ChangeEvent<HTMLInputElement>) => {
    setPass(e.target.value);
  };

  const handleSetName = (type: 'first' | 'last') => (e: ChangeEvent<HTMLInputElement>) => {
    if (type === 'first') return setNameFirst(e.target.value);
    return setNameLast(e.target.value);
  };

  const handleSetHasConsent = (e: ChangeEvent<HTMLInputElement>) => {
    setHasConsent(e.target.checked);
  };

  const handleActivate = async () => {
    try {
      setTried(true);
      await activate({
        token: token as string,
        pass,
        nameFirst,
        nameLast,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message);
      err.message = `Activate error: ${err?.message}`;
      logger.error(err);
    }
  };

  const getIsOpen = (): boolean => {
    // if they've come here without a token, a no go
    if (!token) return true;

    // if activate returned an error
    if (!isLoading && tried && !success) return true;

    // upon success, show result
    if (!isLoading && success) return true;

    return false;
  };

  const getModalTitle = (): string => {
    // if they've come here without a token, a no go
    if (!token) return 'Invalid token';

    // if activate returned an error
    if (tried && !success) return 'Server Error';

    // upon success
    if (tried && success) return 'Success';

    return '';
  };

  const getModalContent = () => {
    // if they've come here without a token, a no go
    if (!token)
      return (
        <div className={styles.notifyContainer}>
          <div className={styles.notifyIcon}>
            <FontAwesomeIcon icon={faExclamationTriangle as IconProp} color="orange" />
          </div>
          <div className={styles.notifyText}>
            Problem with the unique token in the URL. Please make sure you click on the link in your
            email or copy and paste the entire URL into your browser.
          </div>
        </div>
      );

    // if activate returned an error
    if (tried && !success)
      return (
        <div className={styles.notifyContainer}>
          <div className={styles.notifyIcon}>
            <FontAwesomeIcon icon={faExclamationCircle as IconProp} color="red" />
          </div>
          <div className={styles.notifyText}>
            There was an error processing your request: <br />
            <br />
            {error}
          </div>
        </div>
      );

    return (
      <div className={styles.notifyContainer}>
        <div className={styles.notifyIcon}>
          <FontAwesomeIcon icon={faCheckCircle as IconProp} color="green" />
        </div>
        <div className={styles.notifyText}>
          Account activated successfully. You may now log in with your email and password.
          <br />
          <br />
          <Link to="/login">
            <Button>Log In</Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Input.Field
        name="decoded-email"
        className={styles.input}
        value={decoded?.email ?? 'Email address not found'}
        autoComplete="decoded-email"
        disabled
      />

      <Input.Field
        name="new-first-name"
        className={styles.input}
        placeholder="First Name"
        value={nameFirst}
        autoComplete="new-first-name"
        onChange={handleSetName('first')}
        disabled={isLoading}
      />

      <Input.Field
        name="new-last-name"
        className={styles.input}
        placeholder="Last Name"
        value={nameLast}
        autoComplete="new-last-name"
        onChange={handleSetName('last')}
        disabled={isLoading}
      />

      <Input.Password
        name="new-password"
        className={styles.input}
        placeholder="Password"
        type="password"
        value={pass}
        autoComplete="new-password"
        onChange={handleSetPass}
        disabled={isLoading}
      />

      <div className={styles.consentContainer}>
        <Checkbox name="consent" onChange={handleSetHasConsent} checked={hasConsent} />
        <p>
          By clicking Activate Account you agree to the Limbic{' '}
          <a
            className={styles.link}
            href="https://limbic-clinic-admin-portal-versioning.s3.eu-west-2.amazonaws.com/policies/Limbic_Care_Terms_of_Use_(UK).pdf">
            Terms of Use
          </a>
          <br />
          and the processing of your data in line with the{' '}
          <a
            className={styles.link}
            href="https://limbic-clinic-admin-portal-versioning.s3.eu-west-2.amazonaws.com/policies/Limbic_Care_Privacy_Notice_(UK).pdf">
            Privacy Notice
          </a>
          .
        </p>
      </div>

      <div className="buttons">
        <Button
          variant="dark"
          type="submit"
          onClick={handleActivate}
          disabled={isLoading || !isValidPassword(pass) || !nameFirst || !nameLast || !hasConsent}
          isLoading={isLoading}
          dot="green">
          Activate Account
        </Button>
      </div>

      <Modal
        isOpen={getIsOpen()}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        onClose={noop}
        title={getModalTitle()}
        contentProps={{}}
        style={{}}>
        {getModalContent()}
      </Modal>
    </div>
  );
};

export default UsersActivateView;
